import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import apiClient from '../../../server/server';

// Get base URL from the apiClient configuration
const baseURL = apiClient.defaults.baseURL;

export const profileClaimApiSlice = createApi({
  reducerPath: 'profileClaimApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      // Add auth token if available
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['ProfileClaim'],
  endpoints: (builder) => ({
    // Create profile claim with email (sends invitation email)
    createProfileClaim: builder.mutation({
      query: ({ targetProfileId, email, reason }) => ({
        url: '/profile-claim/create',
        method: 'POST',
        body: { targetProfileId, email, reason },
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Create generic profile claim (no email required)
    createGenericClaim: builder.mutation({
      query: ({ targetProfileId, reason }) => ({
        url: '/profile-claim/create-generic',
        method: 'POST',
        body: { targetProfileId, reason },
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Create auto-claim invitation link (new endpoint)
    createAutoClaimInvitation: builder.mutation({
      query: ({ requestedProfileId, reason }) => ({
        url: '/business-profile-requests/create-auto-claim',
        method: 'POST',
        body: { requestedProfileId, reason },
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Get all profile claims with pagination and filters
    getProfileClaims: builder.query({
      query: ({ page = 1, limit = 10, status, search } = {}) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (status) params.append('status', status);
        if (search) params.append('search', search);
        
        return `/profile-claim/all?${params.toString()}`;
      },
      providesTags: ['ProfileClaim'],
    }),

    // Get single profile claim by ID
    getProfileClaimById: builder.query({
      query: (id) => `/profile-claim/${id}`,
      providesTags: (result, error, id) => [{ type: 'ProfileClaim', id }],
    }),

    // Resend invitation email (for email-based claims only)
    resendInvitation: builder.mutation({
      query: (claimId) => ({
        url: `/profile-claim/${claimId}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Cancel pending claim
    cancelClaim: builder.mutation({
      query: (claimId) => ({
        url: `/profile-claim/${claimId}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Accept profile claim (used by the claiming user)
    acceptClaim: builder.mutation({
      query: (claimId) => ({
        url: '/profile-claim/accept',
        method: 'POST',
        body: { claimId },
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Reject profile claim (used by the claiming user)
    rejectClaim: builder.mutation({
      query: (claimId) => ({
        url: '/profile-claim/reject',
        method: 'POST',
        body: { claimId },
      }),
      invalidatesTags: ['ProfileClaim'],
    }),

    // Get unclaimed profiles (for dropdown selection)
    getUnclaimedProfiles: builder.query({
      query: () => '/profiles/unclaimed',
      providesTags: ['ProfileClaim'],
    }),

    // Get profile claim statistics
    getProfileClaimStats: builder.query({
      query: () => '/profile-claim/stats',
      providesTags: ['ProfileClaim'],
    }),
  }),
});

export const {
  useCreateProfileClaimMutation,
  useCreateGenericClaimMutation,
  useCreateAutoClaimInvitationMutation,
  useGetProfileClaimsQuery,
  useGetProfileClaimByIdQuery,
  useResendInvitationMutation,
  useCancelClaimMutation,
  useAcceptClaimMutation,
  useRejectClaimMutation,
  useGetUnclaimedProfilesQuery,
  useGetProfileClaimStatsQuery,
} = profileClaimApiSlice;